<template>
  <section v-show="active" class="app-modal__section">
    <div class="app-modal__section-head supplier-general__head">
      <div>
        <h4>{{ text.modal.form.general.title }}</h4>
        <p>{{ text.modal.form.general.subtitle }}</p>
      </div>

      <div
        v-if="canShowExistingLogo"
        class="supplier-general__logo-card-wrap"
      >
        <AppLogoBadge
          :src="existingLogoUrl"
          :alt="text.modal.form.general.fields.logoPreviewAlt"
          variant="modal"
          :show-remove="true"
          :remove-aria-label="text.modal.form.general.actions.removeLogo"
          :remove-title="text.modal.form.general.actions.removeLogo"
          @remove="$emit('logo-remove')"
        />
      </div>
    </div>

    <div class="app-modal__grid two-columns">
      <label class="app-modal-field full-width">
        <span>{{ text.modal.form.general.fields.name }}</span>
        <input
          v-model="form.name"
          type="text"
          class="form-control"
          :placeholder="text.modal.form.general.placeholders.name"
          @blur="v$.form.name.$touch()"
          :class="{ 'is-invalid': v$.form.name.$error }"
        />
        <div class="invalid-feedback d-block" v-if="v$.form.name.$error">
          <span v-if="v$.form.name.$errors.length">{{ v$.form.name.$errors[0].$message }}</span>
        </div>
      </label>

      <label class="app-modal-field full-width">
        <span>{{ text.modal.form.general.fields.logo }}</span>

        <div v-if="logoPreviewUrl" class="supplier-general__logo-preview-wrap">
          <AppImageLoader
            :src="logoPreviewUrl"
            :alt="text.modal.form.general.fields.logoPreviewAlt"
            fit="contain"
            class="supplier-general__logo-preview"
          />
          <button
            type="button"
            class="supplier-general__logo-preview-trash"
            :aria-label="text.modal.form.general.actions.removeLogo"
            :title="text.modal.form.general.actions.removeLogo"
            @click="$emit('logo-remove')"
          >
            <i class="fa fa-trash" aria-hidden="true"></i>
          </button>
        </div>

        <div class="supplier-general__file-upload">
          <input
            ref="logoInput"
            type="file"
            class="supplier-general__file-input"
            accept="image/*"
            @change="onLogoInputChange"
          />
          <button
            type="button"
            class="app-header-btn app-header-btn--secondary supplier-general__file-trigger"
            @click="openLogoPicker"
          >
            {{ text.modal.form.general.actions.selectLogo }}
          </button>
          <span class="supplier-general__file-name">
            {{ logoFileName || text.modal.form.general.placeholders.logoEmpty }}
          </span>
          <button
            v-if="logoPreviewUrl"
            type="button"
            class="supplier-general__file-clear"
            :aria-label="text.modal.form.general.actions.removeLogo"
            :title="text.modal.form.general.actions.removeLogo"
            @click="$emit('logo-remove')"
          >
            <i class="fa fa-trash" aria-hidden="true"></i>
          </button>
        </div>
      </label>

      <AppClassCategorySelector
        v-model:class-option="form.classOption"
        v-model:category-option="form.categoryOption"
        :class-validation="v$.form.classOption"
        :category-validation="v$.form.categoryOption"
        @touch-class-option="v$.form.classOption.$touch()"
        @touch-category-option="v$.form.categoryOption.$touch()"
      />

      <label class="app-modal-field">
        <span>{{ text.modal.form.general.fields.website }}</span>
        <input v-model="form.website" type="text" class="form-control" :placeholder="text.modal.form.general.placeholders.website" />
      </label>

      <div class="app-modal-field">
        <span>{{ text.modal.form.general.fields.status }}</span>
        <multiselect
          v-model="form.status"
          :options="statusOptions"
          :show-labels="false"
          :searchable="false"
          :allow-empty="false"
          :placeholder="text.modal.form.general.placeholders.status"
          class="app-modal-select"
        >
          <template #noResult>
            <span>{{ text.common.noOptionAvailable }}</span>
          </template>
          <template #noOptions>
            <span>{{ text.common.noOptionAvailable }}</span>
          </template>
        </multiselect>
      </div>

      <label class="app-modal-field full-width">
        <span>{{ text.modal.form.general.fields.comment }}</span>
        <textarea
          v-model="form.comment"
          class="form-control app-modal-field__textarea"
          rows="4"
          :placeholder="text.modal.form.general.placeholders.comment"
        ></textarea>
      </label>
    </div>
  </section>
</template>

<script>
import AppClassCategorySelector from '@/components/common/selectors/AppClassCategorySelector.vue';
import AppImageLoader from '@/components/common/AppImageLoader.vue';
import AppLogoBadge from '@/components/common/AppLogoBadge.vue';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('suppliers');

export default {
  name: 'SupplierSectionGeneral',
  components: {
    AppClassCategorySelector,
    AppImageLoader,
    AppLogoBadge,
  },
  emits: ['logo-change', 'logo-remove'],
  props: {
    active: { type: Boolean, default: false },
    form: { type: Object, required: true },
    v$: { type: Object, required: true },
    isEditMode: { type: Boolean, default: false },
    existingLogoUrl: { type: String, default: '' },
    logoPreviewUrl: { type: String, default: '' },
    logoMarkedForDelete: { type: Boolean, default: false },
    logoFileName: { type: String, default: '' },
    statusOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      text,
    };
  },
  computed: {
    canShowExistingLogo() {
      if (!this.isEditMode || this.logoMarkedForDelete) {
        return false;
      }

      return String(this.existingLogoUrl || '').trim() !== '';
    },
  },
  methods: {
    openLogoPicker() {
      const input = this.$refs.logoInput;
      if (input && typeof input.click === 'function') {
        input.click();
      }
    },
    onLogoInputChange(event) {
      const target = event?.target;
      const file = target?.files && target.files[0] ? target.files[0] : null;
      this.$emit('logo-change', file);
    },
  },
};
</script>

<style scoped>
.supplier-general__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.supplier-general__logo-card-wrap {
  display: inline-flex;
}

.supplier-general__logo-preview-wrap {
  position: relative;
  margin-top: 12px;
  border: 1px dashed #cfd8e3;
  border-radius: 10px;
  background: #f8fbff;
  padding: 10px;
  min-height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.supplier-general__logo-preview {
  width: 100%;
  height: 200px;
}

.supplier-general__logo-preview-trash {
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 30px;
  height: 30px;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: rgba(248, 250, 252, 0.95);
  color: #e11d48;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.supplier-general__file-upload {
  border: 1px solid rgba(153, 152, 153, 0.3);
  border-radius: 8px;
  min-height: 44px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ffffff;
  margin-top: 10px;
}

.supplier-general__file-input {
  display: none;
}

.supplier-general__file-trigger {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 12px;
  flex-shrink: 0;
}

.supplier-general__file-name {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  font-size: 12px;
  color: #4b5563;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.supplier-general__file-clear {
  width: 28px;
  height: 28px;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: rgba(248, 250, 252, 0.95);
  color: #e11d48;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.supplier-general__logo-preview-trash:hover,
.supplier-general__file-clear:hover {
  color: #be123c;
  background: #ffffff;
}
</style>
